"use client";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

/* ── animated counter hook ── */
function useCounter(target, duration = 2200, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const num = parseFloat(target.replace(/[^0-9.]/g, ""));
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * num));
      if (p < 1) requestAnimationFrame(step);
      else setCount(num);
    };
    requestAnimationFrame(step);
  }, [start]);
  const prefix = target.startsWith("$") ? "$" : "";
  const suffix = target.replace(/[$0-9.]/g, "");
  return `${prefix}${count}${suffix}`;
}

/* ── scroll reveal hook ── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ── typewriter ── */
function Typewriter({ words, speed = 75, pause = 2200 }) {
  const [text, setText] = useState("");
  const [wi, setWi] = useState(0);
  const [ci, setCi] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const w = words[wi];
    if (!del && ci < w.length) {
      const t = setTimeout(() => setCi(c => c + 1), speed);
      return () => clearTimeout(t);
    }
    if (!del && ci === w.length) {
      const t = setTimeout(() => setDel(true), pause);
      return () => clearTimeout(t);
    }
    if (del && ci > 0) {
      const t = setTimeout(() => setCi(c => c - 1), speed / 2);
      return () => clearTimeout(t);
    }
    if (del && ci === 0) { setDel(false); setWi(w2 => (w2 + 1) % words.length); }
  }, [ci, del, wi]);
  useEffect(() => setText(words[wi].slice(0, ci)), [ci, wi]);
  return <span>{text}<span style={{ animation: "blink 1s step-end infinite", color: "#a78bfa" }}>|</span></span>;
}

/* ── canvas particle field ── */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const pts = Array.from({ length: 55 }, () => ({
      x: Math.random() * c.width, y: Math.random() * c.height,
      r: Math.random() * 1.4 + 0.4,
      vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
      o: Math.random() * 0.4 + 0.1,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x = (p.x + p.vx + c.width) % c.width;
        p.y = (p.y + p.vy + c.height) % c.height;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139,92,246,${p.o})`; ctx.fill();
      });
      pts.forEach((a, i) => pts.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 110) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(99,102,241,${0.12 * (1 - d / 110)})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />;
}

/* ── reveal wrapper ── */
function Reveal({ children, delay = 0, style = {}, className = "" }) {
  const [ref, v] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: v ? 1 : 0,
      transform: v ? "translateY(0)" : "translateY(36px)",
      transition: `opacity 0.75s ease ${delay}ms, transform 0.75s ease ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

/* ── stat card with counter ── */
function StatCard({ value, label, started }) {
  const display = useCounter(value, 2400, started);
  return (
    <div style={{
      background: "#060818", padding: "24px 32px",
      transition: "background 0.4s, transform 0.3s",
      cursor: "default",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = "#0d1130"; e.currentTarget.style.transform = "translateY(-4px)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#060818"; e.currentTarget.style.transform = "none"; }}>
      <div style={{
        fontSize: 28, fontWeight: 900, background: "linear-gradient(135deg,#a78bfa,#6366f1)",
        WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
      }}>{display}</div>
      <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>{label}</div>
    </div>
  );
}

/* ── Solar System — fully canvas-driven so planets ACTUALLY move ── */
function SolarSystem() {
  const canvasRef = useRef(null);
  const tooltipRef = useRef(null);
  const stateRef = useRef({
    angles: [0.2, 1.1, 2.4, 0.7],   // one angle per orbit, mutable
    hovered: null,
  });

  const ORBITS = [
    { r:88,  spd: 0.012, ringCol:"rgba(124,58,237,0.4)", dash:[5,7],
      planets:[
        { label:"React",      color:"#61dafb", text:"⚛",  sz:19 },
        { label:"Node.js",    color:"#68a063", text:"N",  sz:19 },
      ]},
    { r:145, spd:-0.007, ringCol:"rgba(99,102,241,0.35)", dash:[4,8],
      planets:[
        { label:"Next.js",    color:"#ffffff", text:"▲",  sz:18 },
        { label:"TypeScript", color:"#3178c6", text:"TS", sz:18 },
        { label:"PostgreSQL", color:"#336791", text:"PG", sz:18 },
      ]},
    { r:205, spd: 0.005, ringCol:"rgba(59,130,246,0.28)", dash:[3,9],
      planets:[
        { label:"Python",     color:"#ffd43b", text:"Py", sz:17 },
        { label:"Docker",     color:"#2496ed", text:"🐳", sz:17 },
        { label:"AWS",        color:"#ff9900", text:"☁",  sz:17 },
        { label:"Redis",      color:"#dc382d", text:"R",  sz:17 },
      ]},
    { r:265, spd:-0.0035, ringCol:"rgba(14,165,233,0.22)", dash:[3,10],
      planets:[
        { label:"GraphQL",    color:"#e535ab", text:"GQ", sz:16 },
        { label:"Stripe",     color:"#635bff", text:"S",  sz:16 },
        { label:"Flutter",    color:"#54c5f8", text:"Fl", sz:16 },
        { label:"MongoDB",    color:"#47a248", text:"M",  sz:16 },
        { label:"Firebase",   color:"#ffca28", text:"🔥", sz:16 },
      ]},
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    /* ─ resize helper ─ */
    const setSize = () => {
      const W = canvas.parentElement.offsetWidth;
      canvas.width  = W;
      canvas.height = W;          // square
    };
    setSize();
    const ro = new ResizeObserver(setSize);
    ro.observe(canvas.parentElement);

    /* ─ draw the SoftCoders logo at center ─ */
    const drawLogo = (cx, cy, pulse) => {
      const R = 46 + pulse * 2;
      // outer glow
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 2.8);
      grd.addColorStop(0, `rgba(124,58,237,${0.35 + pulse * 0.15})`);
      grd.addColorStop(1, "rgba(124,58,237,0)");
      ctx.beginPath(); ctx.arc(cx, cy, R * 2.8, 0, Math.PI*2);
      ctx.fillStyle = grd; ctx.fill();

      // dark circle bg
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI*2);
      const bg = ctx.createLinearGradient(cx-R, cy-R, cx+R, cy+R);
      bg.addColorStop(0, "#1e1b4b"); bg.addColorStop(1, "#0f172a");
      ctx.fillStyle = bg; ctx.fill();

      // border ring
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI*2);
      ctx.strokeStyle = `rgba(124,58,237,${0.7 + pulse * 0.3})`;
      ctx.lineWidth = 2; ctx.stroke();

      // ── Actual SoftCoders logo shape ──
      // Outer C-arc (left opening)
      const s = R * 0.72;
      ctx.save(); ctx.translate(cx, cy);

      // Outer circle with gap on right
      ctx.beginPath();
      ctx.arc(0, 0, s, Math.PI*0.15, Math.PI*1.85);
      const lg1 = ctx.createLinearGradient(-s, -s, s, s);
      lg1.addColorStop(0, "#a78bfa"); lg1.addColorStop(1, "#60a5fa");
      ctx.strokeStyle = lg1; ctx.lineWidth = s*0.22; ctx.lineCap="round"; ctx.stroke();

      // Inner reverse arc
      const si = s * 0.52;
      ctx.beginPath();
      ctx.arc(0, 0, si, Math.PI*1.1, Math.PI*0.9, true);
      const lg2 = ctx.createLinearGradient(s, s, -s, -s);
      lg2.addColorStop(0, "#a78bfa"); lg2.addColorStop(1, "#60a5fa");
      ctx.strokeStyle = lg2; ctx.lineWidth = si*0.28; ctx.lineCap="round"; ctx.stroke();

      ctx.restore();

      // "SC" text below icon
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = `bold ${R*0.22}px 'Segoe UI',sans-serif`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("SoftCoders", cx, cy + R + 14);
    };

    /* ─ draw one planet ─ */
    const drawPlanet = (px, py, planet, isHov) => {
      const r = planet.sz * (isHov ? 1.45 : 1);

      // glow
      if (isHov) {
        const g = ctx.createRadialGradient(px, py, 0, px, py, r * 2.5);
        g.addColorStop(0, planet.color + "55");
        g.addColorStop(1, planet.color + "00");
        ctx.beginPath(); ctx.arc(px, py, r*2.5, 0, Math.PI*2);
        ctx.fillStyle = g; ctx.fill();
      }

      // circle
      ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI*2);
      const bg2 = ctx.createRadialGradient(px - r*0.3, py - r*0.3, 0, px, py, r);
      if (isHov) {
        bg2.addColorStop(0, planet.color + "66");
        bg2.addColorStop(1, planet.color + "22");
      } else {
        bg2.addColorStop(0, "#1a1f3d");
        bg2.addColorStop(1, "#0d1028");
      }
      ctx.fillStyle = bg2; ctx.fill();
      ctx.strokeStyle = isHov ? planet.color : planet.color + "66";
      ctx.lineWidth = isHov ? 1.8 : 1.2; ctx.stroke();

      // text / icon
      ctx.fillStyle = isHov ? "#fff" : planet.color;
      ctx.font = `bold ${r * 0.9}px 'Segoe UI',monospace`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(planet.text, px, py);

      // tooltip
      if (isHov) {
        const pad = 8, th = 22, tw = ctx.measureText(planet.label).width + pad*2;
        const tx = px - tw/2, ty = py + r + 6;
        ctx.beginPath();
        ctx.roundRect(tx, ty, tw, th, 6);
        ctx.fillStyle = "rgba(8,10,28,0.92)"; ctx.fill();
        ctx.strokeStyle = planet.color + "55"; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.font = `600 11px 'Segoe UI',sans-serif`;
        ctx.fillText(planet.label, px, ty + th/2);
      }
    };

    /* ─ asteroid belt ─ */
    const drawAsteroids = (cx, cy, t) => {
      for (let i = 0; i < 22; i++) {
        const a = (Math.PI*2/22)*i + t*0.0008;
        const r = 174 + Math.sin(i*1.9)*7;
        const ax = cx + r*Math.cos(a), ay = cy + r*Math.sin(a);
        ctx.beginPath(); ctx.arc(ax, ay, i%4===0 ? 1.8 : 1.1, 0, Math.PI*2);
        ctx.fillStyle = `rgba(167,139,250,${0.12 + (i%5)*0.06})`; ctx.fill();
      }
    };

    /* ─ main loop ─ */
    let t = 0;
    let raf;
    const loop = () => {
      t++;
      const S = stateRef.current;
      const W = canvas.width;
      const cx = W/2, cy = W/2;
      const scale = W / 600; // responsive scale factor

      ctx.clearRect(0, 0, W, W);

      // advance angles
      ORBITS.forEach((orb, i) => { S.angles[i] += orb.spd; });

      // draw orbit rings
      ORBITS.forEach(orb => {
        ctx.beginPath();
        ctx.arc(cx, cy, orb.r * scale, 0, Math.PI*2);
        ctx.strokeStyle = orb.ringCol;
        ctx.lineWidth = 1;
        ctx.setLineDash(orb.dash);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // asteroid belt
      drawAsteroids(cx, cy, t);

      // draw planets
      ORBITS.forEach((orb, oi) => {
        const count = orb.planets.length;
        orb.planets.forEach((planet, pi) => {
          const base = (Math.PI*2/count)*pi;
          const angle = base + S.angles[oi];
          const px = cx + orb.r * scale * Math.cos(angle);
          const py = cy + orb.r * scale * Math.sin(angle);
          const isHov = S.hovered?.oi===oi && S.hovered?.pi===pi;
          drawPlanet(px, py, { ...planet, sz: planet.sz * scale }, isHov);
        });
      });

      // draw center logo
      const pulse = (Math.sin(t * 0.03) + 1) / 2;
      drawLogo(cx, cy, pulse);

      raf = requestAnimationFrame(loop);
    };
    loop();

    /* ─ hover detection ─ */
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
      const my = (e.clientY - rect.top)  * (canvas.height / rect.height);
      const W = canvas.width, scale = W/600;
      const cx = W/2, cy = W/2;
      let found = null;
      ORBITS.forEach((orb, oi) => {
        const count = orb.planets.length;
        orb.planets.forEach((planet, pi) => {
          const base = (Math.PI*2/count)*pi;
          const angle = base + stateRef.current.angles[oi];
          const px = cx + orb.r*scale*Math.cos(angle);
          const py = cy + orb.r*scale*Math.sin(angle);
          if (Math.hypot(mx-px, my-py) < planet.sz*scale*1.6) found = {oi,pi};
        });
      });
      stateRef.current.hovered = found;
      canvas.style.cursor = found ? "pointer" : "default";
    };
    const onLeave = () => { stateRef.current.hovered = null; canvas.style.cursor="default"; };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div style={{ position:"relative", width:"100%", aspectRatio:"1/1", maxWidth:560 }}>
      

<Helmet>
  <title>
    SaaS Development Company in India | Software as a Service Solutions | SoftCoders
  </title>

  <meta
    name="description"
    content="SoftCoders is a SaaS development company in India building scalable Software as a Service platforms, cloud applications, subscription-based software, business automation tools, and enterprise SaaS solutions."
  />

  <meta
    name="keywords"
    content="
    SaaS development company,
    software as a service development,
    SaaS application development,
    SaaS software company,
    cloud software development,
    custom SaaS development,
    enterprise SaaS solutions,
    scalable SaaS platforms,
    subscription software development,
    SaaS product development,
    SaaS web applications,
    business automation SaaS,
    startup SaaS development,
    multi tenant SaaS applications,
    cloud based software solutions,
    CRM SaaS development,
    ERP SaaS platforms,
    AI SaaS solutions,
    software company india,
    SoftCoders
    "
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="SoftCoders" />
  <meta name="language" content="English" />

  {/* Canonical */}
  <link
    rel="canonical"
    href="https://softcoders.in/saas-development"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="SaaS Development Company | Software as a Service Solutions | SoftCoders"
  />

  <meta
    property="og:description"
    content="Build scalable SaaS platforms, cloud applications, subscription-based software, and enterprise SaaS systems with SoftCoders."
  />

  <meta
    property="og:url"
    content="https://softcoders.in/saas-development"
  />

  <meta property="og:type" content="website" />

  <meta
    property="og:image"
    content="https://softcoders.in/og-image.jpg"
  />

  {/* Twitter SEO */}
  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="SaaS Development Company | SoftCoders"
  />

  <meta
    name="twitter:description"
    content="SoftCoders develops custom SaaS applications, cloud platforms, subscription software, ERP SaaS systems, and scalable business solutions."
  />

  <meta
    name="twitter:image"
    content="https://softcoders.in/og-image.jpg"
  />

  {/* GEO SEO */}
  <meta name="geo.region" content="IN-HR" />
  <meta name="geo.placename" content="Sirsa, Haryana" />

  {/* Mobile SEO */}
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  />

  {/* Theme */}
  <meta name="theme-color" content="#0a0d1f" />

  {/* Schema Markup */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "SoftwareCompany",
        "name": "SoftCoders",
        "url": "https://softcoders.in",
        "description": "SoftCoders is a SaaS development company providing scalable cloud software, subscription platforms, ERP SaaS systems, CRM applications, and enterprise SaaS solutions.",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Sirsa",
          "addressRegion": "Haryana",
          "addressCountry": "India"
        },
        "serviceType": [
          "SaaS Development",
          "Software as a Service Solutions",
          "Cloud Software Development",
          "Subscription Software Platforms",
          "Enterprise SaaS Applications",
          "Business Automation Systems"
        ]
      }
    `}
  </script>
</Helmet>

      <canvas ref={canvasRef} style={{ width:"100%", height:"100%", display:"block" }} />
      <div ref={tooltipRef} />
    </div>
  );
}

/* ══════════════════════════════════════════ */
export default function SaaSdevelopmentPage() {
  const [statsOn, setStatsOn] = useState(false);
  const statsRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [orb, setOrb] = useState({ x: 0, y: 0 });

  /* mouse parallax orb */
  useEffect(() => {
    const h = (e) => setMouse({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setOrb(o => ({ x: o.x + (mouse.x - o.x) * 0.04, y: o.y + (mouse.y - o.y) * 0.04 })), 16);
    return () => clearInterval(id);
  }, [mouse]);

  /* stats intersection */
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsOn(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  /* inject keyframes once */
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = `
      @keyframes blink        { 0%,100%{opacity:1}50%{opacity:0} }
      @keyframes fadeUp       { from{opacity:0;transform:translateY(32px)}to{opacity:1;transform:none} }
      @keyframes floatA       { 0%,100%{transform:translateY(0) rotate(0deg)}40%{transform:translateY(-22px) rotate(1deg)}70%{transform:translateY(-10px) rotate(-1deg)} }
      @keyframes floatB       { 0%,100%{transform:translateY(0)}50%{transform:translateY(-28px)} }
      @keyframes spinRing     { from{transform:rotate(0)}to{transform:rotate(360deg)} }
      @keyframes shimmer      { 0%{background-position:-200% center}100%{background-position:200% center} }
      @keyframes morphGrad    { 0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%} }
      @keyframes ping         { 0%{transform:scale(1);opacity:.7}100%{transform:scale(2.8);opacity:0} }
      @keyframes pulse        { 0%,100%{opacity:1}50%{opacity:.4} }
      @keyframes barFill      { from{width:0} }
      .shimmer-text{background:linear-gradient(90deg,#a78bfa,#6366f1,#3b82f6,#a78bfa);background-size:250% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:shimmer 3.5s linear infinite}
      .morph-btn{background:linear-gradient(135deg,#7c3aed,#4f46e5,#2563eb,#7c3aed)!important;background-size:300% 300%!important;animation:morphGrad 4s ease infinite!important}
      .float-a{animation:floatA 9s ease-in-out infinite}
      .float-b{animation:floatB 7s ease-in-out infinite}
      .spin-a{animation:spinRing 22s linear infinite}
      .spin-b{animation:spinRing 16s linear infinite reverse}
      .hero-h1-1{animation:fadeUp .8s ease .05s forwards;opacity:0}
      .hero-h1-2{animation:fadeUp .8s ease .2s forwards;opacity:0}
      .hero-h1-3{animation:fadeUp .8s ease .35s forwards;opacity:0}
      .hero-badge{animation:fadeUp .8s ease .5s forwards,floatA 7s ease-in-out 1.5s infinite;opacity:0}
      .hero-p{animation:fadeUp .8s ease .5s forwards;opacity:0}
      .hero-btns{animation:fadeUp .8s ease .65s forwards;opacity:0}
      .feat-row{transition:all .35s ease;border:1px solid rgba(255,255,255,.05)!important}
      .feat-row:hover{background:rgba(124,58,237,.06)!important;border-color:rgba(124,58,237,.35)!important;transform:translateX(6px)}
      .feat-row:hover .feat-num{opacity:1!important}
      .tag{transition:all .3s ease;border:1px solid rgba(124,58,237,.2);color:rgba(167,139,250,.6)}
      .tag:hover{background:rgba(124,58,237,.18);border-color:rgba(167,139,250,.5);color:#c4b5fd;transform:scale(1.06)}
      .tech{transition:all .3s ease}
      .tech:hover{background:rgba(124,58,237,.12)!important;border-color:rgba(124,58,237,.4)!important;transform:translateY(-5px)}
      .why-c{transition:all .35s ease}
      .why-c:hover{background:rgba(124,58,237,.07)!important;border-color:rgba(124,58,237,.35)!important;transform:translateY(-5px)}
      .arch-chip{transition:all .25s ease}
      .arch-chip:hover{transform:scale(1.07)}
      .cta-p{transition:all .4s ease}
      .cta-p:hover{transform:scale(1.04);box-shadow:0 20px 60px rgba(124,58,237,.35)!important}
      .cta-s{transition:all .3s ease}
      .cta-s:hover{border-color:rgba(167,139,250,.4)!important;background:rgba(124,58,237,.05);color:#fff}
      .trust-item{transition:all .3s ease;cursor:default}
      .trust-item:hover .trust-dot{transform:scale(1.4)}
      .trust-dot{transition:transform .3s ease}
      .feat-check{transition:transform .3s ease}
      .feat-check:hover{transform:scale(1.15)}

      /* ── RESPONSIVE LAYOUT ── */

      /* hero section */
      .sc-hero { padding: 80px 64px 64px !important; }
      .sc-hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: center; }
      .sc-solar-extras { display: block; }
      .sc-floating-badges { display: block; }
      .sc-stats { grid-template-columns: repeat(4,1fr) !important; }
      .sc-topbar-right { display: flex !important; }

      /* features */
      .sc-feat { grid-template-columns: 80px 1fr auto !important; padding: 32px !important; }
      .sc-feat-tags { display: flex !important; }

      /* tech */
      .sc-tech-grid { grid-template-columns: repeat(9,1fr) !important; }

      /* why us */
      .sc-why-grid { grid-template-columns: 1fr 1fr !important; gap: 96px !important; }
      .sc-why-cards { grid-template-columns: 1fr 1fr !important; }

      /* sections padding */
      .sc-section { padding: 120px 64px !important; }
      .sc-section-sm { padding: 80px 64px !important; }
      .sc-cta { padding: 120px 64px !important; }

      /* ── TABLET (max 1024px) ── */
      @media (max-width: 1024px) {
        .sc-hero { padding: 60px 40px 48px !important; }
        .sc-hero-grid { grid-template-columns: 1fr 1fr; gap: 24px; }
        .sc-stats { grid-template-columns: repeat(4,1fr) !important; }
        .sc-topbar-right { display: none !important; }
        .sc-feat { grid-template-columns: 60px 1fr !important; padding: 24px !important; }
        .sc-feat-tags { display: none !important; }
        .sc-tech-grid { grid-template-columns: repeat(5,1fr) !important; }
        .sc-why-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
        .sc-why-cards { grid-template-columns: 1fr 1fr !important; }
        .sc-section { padding: 80px 40px !important; }
        .sc-section-sm { padding: 60px 40px !important; }
        .sc-cta { padding: 80px 40px !important; }
      }

      /* ── MOBILE (max 768px) ── */
      @media (max-width: 768px) {
        .sc-hero { padding: 24px 20px 32px !important; min-height: auto !important; }
        .sc-hero-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
        .sc-solar-extras { display: none !important; }
        .sc-floating-badges { display: none !important; }
        .sc-stats { grid-template-columns: repeat(2,1fr) !important; margin-top: 32px !important; }
        .sc-topbar-right { display: none !important; }
        .sc-feat { grid-template-columns: 48px 1fr !important; padding: 18px !important; gap: 16px !important; }
        .sc-feat-tags { display: none !important; }
        .sc-tech-grid { grid-template-columns: repeat(3,1fr) !important; }
        .sc-why-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        .sc-why-cards { grid-template-columns: 1fr !important; }
        .sc-section { padding: 56px 20px !important; }
        .sc-section-sm { padding: 40px 20px !important; }
        .sc-cta { padding: 56px 20px !important; }
        .sc-cta-btns { flex-direction: column !important; align-items: stretch !important; }
        .sc-cta-btns button { width: 100% !important; justify-content: center !important; }
        .sc-trust { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
        .feat-row:hover { transform: none !important; }
        .sc-solar-wrap { max-width: 320px !important; margin: 0 auto !important; }
        .sc-hero-btns-row { flex-direction: column !important; }
        .sc-hero-btns-row button { width: 100% !important; justify-content: center !important; }
      }

      /* ── SMALL MOBILE (max 480px) ── */
      @media (max-width: 480px) {
        .sc-stats { grid-template-columns: repeat(2,1fr) !important; }
        .sc-tech-grid { grid-template-columns: repeat(3,1fr) !important; }
        .sc-why-cards { grid-template-columns: 1fr !important; }
        .sc-section { padding: 48px 16px !important; }
        .sc-cta h2 { font-size: 40px !important; }
      }
    `;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const features = [
    { num:"01", title:"Multi-Tenant Architecture", desc:"Isolated data per tenant, shared infrastructure. Row-level security, subdomain routing, and tenant management that scales to thousands.", tags:["PostgreSQL RLS","Schema Isolation","Subdomain Routing"], g:"from-violet-600 to-purple-600" },
    { num:"02", title:"Subscription Billing & Payments", desc:"Stripe-powered billing with plan tiers, usage-based pricing, trial periods, proration, and revenue dashboards. Dunning & tax handling included.", tags:["Stripe","Usage Billing","Revenue Analytics"], g:"from-blue-600 to-indigo-600" },
    { num:"03", title:"Auth, Roles & Permissions", desc:"SSO, OAuth2, magic links, MFA, and team-based RBAC. Enterprise-grade auth implemented right the first time using battle-tested patterns.", tags:["Auth0 / Clerk","SSO / SAML","RBAC"], g:"from-indigo-600 to-violet-600" },
    { num:"04", title:"Admin & Analytics Dashboards", desc:"Superadmin panels for you, customer dashboards for your users. Real-time metrics, charts, export tools, and white-label theming.", tags:["Real-time Charts","White-label","CSV Export"], g:"from-sky-600 to-blue-600" },
    { num:"05", title:"API-First & Webhook System", desc:"Public REST and GraphQL APIs your customers build on. Webhook delivery with retries, event logs, and a developer portal.", tags:["REST / GraphQL","Webhook Engine","Dev Portal"], g:"from-violet-600 to-blue-600" },
    { num:"06", title:"Scalable DevOps & Infrastructure", desc:"Auto-scaling on AWS or GCP, CI/CD pipelines, database replication, CDN setup, uptime monitoring, and disaster recovery.", tags:["AWS / GCP","Docker / K8s","CI/CD"], g:"from-blue-600 to-cyan-600" },
  ];


  const techs = [
    {l:"Next.js",c:"#0070f3"},{l:"React",c:"#61dafb"},{l:"Node.js",c:"#68a063"},
    {l:"PostgreSQL",c:"#336791"},{l:"Redis",c:"#dc382d"},{l:"Stripe",c:"#635bff"},
    {l:"AWS",c:"#ff9900"},{l:"Docker",c:"#2496ed"},{l:"TypeScript",c:"#3178c6"},
  ];

  const archLayers = [
    { label:"▸ Client Layer", items:["Next.js App","Mobile PWA","Public API Docs"], color:"#7c3aed" },
    { label:"▸ Application Layer", items:["Auth Service","Billing Engine","Tenant Router","Webhook System"], color:"#4f46e5" },
    { label:"▸ Data Layer", items:["PostgreSQL (RLS)","Redis Cache","S3 Storage"], color:"#2563eb" },
    { label:"▸ Infrastructure", items:["AWS ECS","CloudFront CDN","RDS Multi-AZ"], color:"#0ea5e9" },
  ];

  return (
    <div style={{ background:"#060818", color:"#fff", overflow:"hidden", fontFamily:"'Segoe UI', system-ui, sans-serif" }}>

      {/* noise overlay */}
      <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, opacity:.025,
        backgroundImage:`url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize:"200px" }} />

      {/* mouse-follow glow orb */}
      <div style={{ position:"fixed", pointerEvents:"none", zIndex:0, width:600, height:600, borderRadius:"50%",
        left: orb.x - 300, top: orb.y - 300,
        background:"radial-gradient(circle,rgba(124,58,237,.07),transparent 70%)" }} />

      {/* ══ HERO ══ */}
      <section className="sc-hero" style={{ position:"relative", minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"space-between", padding:"80px 64px 64px", overflow:"hidden" }}>
        <ParticleField />

        {/* deep bg glow */}
        <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:0 }}>
          <div className="float-a" style={{ position:"absolute", top:"-10%", left:"20%", width:700, height:700, borderRadius:"50%", opacity:.15, background:"radial-gradient(circle,#7c3aed,transparent 70%)" }} />
          <div className="float-b" style={{ position:"absolute", bottom:"-5%", left:"5%", width:400, height:400, borderRadius:"50%", opacity:.1, background:"radial-gradient(circle,#2563eb,transparent 70%)" }} />
        </div>

        {/* grid lines */}
        <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:0, opacity:.03,
          backgroundImage:"linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)",
          backgroundSize:"80px 80px" }} />

        {/* top bar */}
        <div style={{ position:"relative", zIndex:10, display:"flex", alignItems:"center", justifyContent:"space-between", animation:"fadeUp .7s ease forwards" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ position:"relative" }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:"#a78bfa", animation:"pulse 2s infinite" }} />
              <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:"#a78bfa", animation:"ping 1.5s ease infinite" }} />
            </div>
            <span style={{ color:"#c4b5fd", fontSize:13, letterSpacing:".2em", textTransform:"uppercase", fontWeight:500 }}>SoftCoders / SaaS Division</span>
          </div>
          <div className="sc-topbar-right" style={{ display:"flex", alignItems:"center", gap:8, fontSize:11, color:"rgba(255,255,255,.25)", letterSpacing:".15em", textTransform:"uppercase" }}>
            <span>Est. 2018</span>
            <span style={{ width:32, height:1, background:"rgba(255,255,255,.15)", display:"inline-block" }} />
            <span>200+ Products Shipped</span>
          </div>
        </div>

        {/* ── TWO COLUMN: headline left | solar system right ── */}
        <div className="sc-hero-grid" style={{ position:"relative", zIndex:10, flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:32, alignItems:"center", marginTop:32 }}>

          {/* LEFT — headline */}
          <div>
            <div style={{ position:"relative" }}>
              <div style={{ position:"absolute", top:-20, left:0, fontSize:"clamp(80px,12vw,180px)", fontWeight:900, lineHeight:1, color:"rgba(255,255,255,.012)", userSelect:"none", pointerEvents:"none" }}>SAAS</div>
              <h1 style={{ fontSize:"clamp(40px,5.5vw,80px)", fontWeight:900, lineHeight:.95, letterSpacing:"-2px", position:"relative" }}>
                <span className="hero-h1-1" style={{ display:"block" }}>Software</span>
                <span className="hero-h1-2" style={{ display:"block" }}>as a</span>
                <span className="hero-h1-3 shimmer-text" style={{ display:"block" }}>Service.</span>
              </h1>
            </div>

            <p className="hero-p" style={{ marginTop:28, color:"#94a3b8", fontSize:17, lineHeight:1.8, maxWidth:480 }}>
              We build production-ready SaaS platforms for{" "}
              <span style={{ color:"#c4b5fd", fontWeight:600 }}>
                <Typewriter words={["Startups","Enterprises","Founders","Scale-ups","Your Business"]} />
              </span>
              {" "}— billing, auth, multi-tenancy, and everything in between.
            </p>

            {/* feature pills */}
            <div className="hero-p" style={{ display:"flex", flexWrap:"wrap", gap:10, marginTop:24 }}>
              {["Multi-Tenant","Stripe Billing","SSO & Auth","REST APIs","CI/CD DevOps","White-Label"].map((pill,i) => (
                <span key={i} style={{
                  padding:"6px 14px", borderRadius:999, fontSize:12, fontWeight:500,
                  border:"1px solid rgba(124,58,237,.3)", color:"#c4b5fd",
                  background:"rgba(124,58,237,.08)", backdropFilter:"blur(8px)",
                  animation:`fadeUp .6s ease ${0.6 + i * 0.08}s forwards`, opacity:0,
                }}>{pill}</span>
              ))}
            </div>

            <div className="hero-btns sc-hero-btns-row" style={{ display:"flex", flexWrap:"wrap", gap:14, marginTop:36 }}>
              <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="morph-btn" style={{
                padding:"15px 28px", borderRadius:14, fontWeight:700, fontSize:16,
                border:"none", color:"#fff", cursor:"pointer", position:"relative", overflow:"hidden",
                display:"flex", alignItems:"center", gap:8,
              }}
                onMouseEnter={e => { e.currentTarget.querySelector(".arr").style.transform = "translateX(4px)"; }}
                onMouseLeave={e => { e.currentTarget.querySelector(".arr").style.transform = "none"; }}>
                Build My SaaS Platform
                <span className="arr" style={{ transition:"transform .3s" }}>→</span>
              </a>

               <a
    href="/services/software-development"
 className="cta-s" style={{
                padding:"15px 28px", borderRadius:14,
                border:"1px solid rgba(255,255,255,.15)",
                background:"transparent", color:"rgba(255,255,255,.8)",
                fontSize:16, fontWeight:500, cursor:"pointer",
              }}>View Case Studies → </a>
            </div>
          </div>

          {/* RIGHT — solar system + floating extras */}
          <div className="sc-solar-extras" style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div className="sc-solar-wrap">
            <SolarSystem />
            </div>

            {/* floating badges */}
            <div className="sc-floating-badges">
            {/* floating code snippet — top left of solar area */}
            <div style={{
              position:"absolute", top:20, left:-10, zIndex:30,
              background:"rgba(8,10,28,0.92)", border:"1px solid rgba(124,58,237,0.35)",
              borderRadius:14, padding:"12px 16px", backdropFilter:"blur(16px)",
              fontFamily:"monospace", fontSize:11, lineHeight:1.7,
              boxShadow:"0 8px 32px rgba(124,58,237,0.15)",
              animation:"fadeUp .8s ease .9s forwards, floatB 7s ease-in-out 1.5s infinite",
              opacity:0, minWidth:190,
            }}>
              <div style={{ color:"#475569", marginBottom:4, fontSize:10 }}>// saas.config.ts</div>
              <div><span style={{color:"#a78bfa"}}>tenant</span><span style={{color:"#64748b"}}>(</span><span style={{color:"#34d399"}}>"acme-corp"</span><span style={{color:"#64748b"}}>)</span></div>
              <div><span style={{color:"#a78bfa"}}>&nbsp;.billing</span><span style={{color:"#64748b"}}>(</span><span style={{color:"#34d399"}}>"stripe"</span><span style={{color:"#64748b"}}>)</span></div>
              <div><span style={{color:"#a78bfa"}}>&nbsp;.auth</span><span style={{color:"#64748b"}}>(</span><span style={{color:"#34d399"}}>"sso"</span><span style={{color:"#64748b"}}>)</span></div>
              <div><span style={{color:"#a78bfa"}}>&nbsp;.deploy</span><span style={{color:"#64748b"}}>()</span></div>
              <div style={{color:"#22d3ee", marginTop:2}}>✓ <span style={{color:"#64748b"}}>Live in 6 weeks</span></div>
            </div>

            {/* uptime badge — bottom right */}
            <div style={{
              position:"absolute", bottom:30, right:-10, zIndex:30,
              background:"rgba(8,10,28,0.92)", border:"1px solid rgba(34,197,94,0.4)",
              borderRadius:14, padding:"12px 16px", backdropFilter:"blur(16px)",
              boxShadow:"0 8px 32px rgba(34,197,94,0.1)",
              animation:"fadeUp .8s ease 1.1s forwards, floatA 8s ease-in-out 2s infinite",
              opacity:0,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ position:"relative" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:"#22c55e" }} />
                  <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:"#22c55e", animation:"ping 1.5s ease infinite" }} />
                </div>
                <span style={{ color:"#22c55e", fontSize:12, fontWeight:700 }}>All Systems Operational</span>
              </div>
              <div style={{ color:"#64748b", fontSize:11, marginTop:4 }}>Uptime: <span style={{color:"#fff",fontWeight:700}}>99.99%</span> · 30 days</div>
            </div>

            {/* tenants badge — top right */}
            <div style={{
              position:"absolute", top:55, right:-15, zIndex:30,
              background:"rgba(8,10,28,0.92)", border:"1px solid rgba(99,102,241,0.4)",
              borderRadius:14, padding:"10px 16px", backdropFilter:"blur(16px)",
              animation:"fadeUp .8s ease 1.3s forwards, floatB 9s ease-in-out 2.5s infinite",
              opacity:0,
            }}>
              <div style={{ color:"#818cf8", fontSize:11, fontWeight:600 }}>Active Tenants</div>
              <div style={{ fontSize:22, fontWeight:900, color:"#fff", letterSpacing:"-1px" }}>12,847</div>
              <div style={{ color:"#22c55e", fontSize:10 }}>↑ 23% this month</div>
            </div>
            </div>{/* end sc-floating-badges */}
          </div>{/* end sc-solar-extras */}
        </div>{/* end sc-hero-grid */}

        {/* stats strip */}
        <div ref={statsRef} className="sc-stats" style={{
          position:"relative", zIndex:10,
          display:"grid", gridTemplateColumns:"repeat(4,1fr)",
          gap:1, background:"rgba(255,255,255,.05)",
          borderRadius:20, overflow:"hidden", marginTop:48,
          border:"1px solid rgba(255,255,255,.05)",
        }}>
          {[["$2.4B+","SaaS Revenue Generated"],["120+","Platforms Launched"],["99.99%","Avg Uptime SLA"],["6 Weeks","MVP to Launch"]].map(([v,l],i) => (
            <StatCard key={i} value={v} label={l} started={statsOn} />
          ))}
        </div>
      </section>

      {/* ══ FEATURES ══ */}
      <section className="sc-section" style={{ padding:"120px 64px", borderTop:"1px solid rgba(255,255,255,.05)" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <Reveal style={{ display:"flex", alignItems:"flex-end", gap:24, marginBottom:72 }}>
            <div>
              <p style={{ color:"#a78bfa", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:12 }}>Core Capabilities</p>
              <h2 style={{ fontSize:"clamp(36px,5vw,56px)", fontWeight:900, lineHeight:1.1 }}>
                Everything Your<br />
                <span className="shimmer-text">SaaS Needs.</span>
              </h2>
            </div>
            <div style={{ flex:1, height:1, background:"linear-gradient(90deg,rgba(124,58,237,.3),transparent)" }} />
          </Reveal>

          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {features.map((f, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className="feat-row sc-feat" style={{
                  display:"grid", gridTemplateColumns:"80px 1fr auto",
                  gap:32, alignItems:"center", padding:32,
                  borderRadius:20, background:"rgba(255,255,255,.02)",
                  cursor:"default",
                }}>
                  <div className="feat-num" style={{
                    fontSize:44, fontWeight:900, opacity:.28,
                    background:`linear-gradient(135deg,${f.g.includes("violet")?"#7c3aed":"#2563eb"},${f.g.includes("purple")?"#9333ea":"#6366f1"})`,
                    WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent",
                    transition:"opacity .4s",
                  }}>{f.num}</div>
                  <div>
                    <h3 style={{ fontSize:18, fontWeight:700, marginBottom:8 }}>{f.title}</h3>
                    <p style={{ color:"#64748b", lineHeight:1.7, fontSize:14, maxWidth:600 }}>{f.desc}</p>
                  </div>
                  <div className="sc-feat-tags" style={{ display:"flex", flexDirection:"column", gap:8, alignItems:"flex-end" }}>
                    {f.tags.map((tag, j) => (
                      <span key={j} className="tag" style={{ padding:"4px 12px", borderRadius:999, fontSize:11, whiteSpace:"nowrap", cursor:"pointer" }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TECH STACK ══ */}
      <section className="sc-section-sm" style={{ padding:"80px 64px", borderTop:"1px solid rgba(255,255,255,.05)" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <Reveal>
            <p style={{ textAlign:"center", color:"#334155", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:48 }}>Our SaaS Technology Stack</p>
          </Reveal>
          <div className="sc-tech-grid" style={{ display:"grid", gridTemplateColumns:"repeat(9,1fr)", gap:12 }}>
            {techs.map((t, i) => (
              <Reveal key={i} delay={i * 40}>
                <div className="tech" style={{ padding:12, borderRadius:14, background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.05)", textAlign:"center", cursor:"default" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:t.c, margin:"0 auto 8px", transition:"transform .3s" }} />
                  <div style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{t.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY US ══ */}
      <section className="sc-section" style={{ padding:"120px 64px", borderTop:"1px solid rgba(255,255,255,.05)" }}>
        <div className="sc-why-grid" style={{ maxWidth:1200, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:96, alignItems:"center" }}>
          <Reveal>
            <p style={{ color:"#a78bfa", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:16 }}>Why Us</p>
            <h2 style={{ fontSize:"clamp(32px,4vw,48px)", fontWeight:900, lineHeight:1.15 }}>
              We've Shipped<br />Real SaaS.<br />
              <span className="shimmer-text">Not Just Code.</span>
            </h2>
            <p style={{ color:"#64748b", marginTop:28, lineHeight:1.8, fontSize:16 }}>
              Most agencies deliver code. We deliver products. Every SaaS we build ships with the foundational infrastructure that founders need to grow — not just a pretty UI and a few API endpoints.
            </p>
            <p style={{ color:"#64748b", marginTop:16, lineHeight:1.8 }}>
              Our SaaS specialists have built platforms serving millions of users across fintech, healthtech, edtech, and B2B software — we know the pitfalls before they cost you money.
            </p>
            <div style={{ marginTop:36, display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              {[
                ["SaaS-First Architecture","Built for multi-tenancy from day one"],
                ["Revenue-Ready Billing","Stripe setup, not an afterthought"],
                ["Founder-Friendly Process","Weekly demos, no agency jargon"],
                ["Post-Launch Partner","We stick around after go-live"],
              ].map(([title, sub], i) => (
                <div key={i} className="why-c" style={{ padding:20, borderRadius:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.05)", cursor:"default" }}>
                  <div style={{ width:24, height:2, background:"linear-gradient(90deg,#7c3aed,#3b82f6)", marginBottom:12 }} />
                  <div style={{ fontWeight:700, fontSize:13 }}>{title}</div>
                  <div style={{ color:"#475569", fontSize:11, marginTop:4, lineHeight:1.5 }}>{sub}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* arch diagram */}
          <Reveal delay={180}>
            <div style={{ position:"relative" }}>
              <div className="float-a" style={{ position:"absolute", inset:-16, borderRadius:28, opacity:.18, filter:"blur(30px)", background:"linear-gradient(135deg,#7c3aed,#2563eb)", pointerEvents:"none" }} />
              <div style={{ position:"relative", borderRadius:20, border:"1px solid rgba(255,255,255,.1)", background:"#080c1e", overflow:"hidden" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"16px 24px", borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    {["#ef4444","#eab308","#22c55e"].map((c,i) => (
                      <div key={i} style={{ width:10, height:10, borderRadius:"50%", background:c, opacity:.7, cursor:"pointer", transition:"opacity .2s" }}
                        onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                        onMouseLeave={e => e.currentTarget.style.opacity = ".7"} />
                    ))}
                  </div>
                  <span style={{ fontSize:11, color:"#475569", fontFamily:"monospace" }}>saas-architecture.diagram</span>
                </div>
                <div style={{ padding:32, fontFamily:"monospace", fontSize:11 }}>
                  {archLayers.map((layer, i) => (
                    <div key={i} style={{ marginBottom: i < 3 ? 16 : 0 }}>
                      <div style={{ color:layer.color, fontWeight:700, marginBottom:8 }}>{layer.label}</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8, paddingLeft:12 }}>
                        {layer.items.map((item, j) => (
                          <div key={j} className="arch-chip" style={{
                            padding:"6px 12px", borderRadius:8,
                            border:`1px solid ${layer.color}30`,
                            background:`${layer.color}12`,
                            color:"#94a3b8", fontSize:10, cursor:"default",
                          }}>{item}</div>
                        ))}
                      </div>
                      {i < 3 && <div style={{ paddingLeft:12, marginTop:8, color:"#334155", fontSize:10 }}>│ ↓ ↓ ↓</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ══ */}
      
    </div>
  );
}